import { Box, Divider, Grid, styled } from '@mui/material';
import { gql } from 'graphql-request';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useCampSiteChangeLogTotalByUserIdQuery } from '@/__generated__/public-graphql';
import {
  useGetWinGachaQuery,
  useMyPointQuery,
} from '@/__generated__/user-graphql';
import Button from '@/contents/General/Button';
import Chip from '@/contents/General/Chip';
import Icon from '@/contents/General/Icon';
import { Board } from '@/contents/General/Panels';
import Stack from '@/contents/General/Stack';
import Typography from '@/contents/General/Typography';
import UserContents from '@/contents/User/UserContents';
import UserImage from '@/contents/User/UserImage';
import OptimizedImage from '@/modules/General/OptimizedImage';
import useGetLoginUser from '@/modules/User/user/useGetLoginUser';
import useUserNotificationNum from '@/modules/User/useUserNotificationNum';
import theme from '@/styles/theme';
import type { PublicUser } from '@/types/domain';

const Content = styled(Box)`
  position: relative;
  margin-top: 57px;
  z-index: 1;
`;

const CountChip = styled(Box)`
  padding: 0 5px;
  border-radius: 8px;
  background: #e2edf5;
`;

const Notification = ({ userId }: { userId: string }) => {
  const { totalUnReadNum } = useUserNotificationNum();

  return (
    <>
      {totalUnReadNum !== 0 && (
        <Chip
          label={totalUnReadNum}
          chipColor="red"
          sx={{ position: 'absolute', top: '0px', right: '2px' }}
        />
      )}
      <Stack column center space={0.5}>
        <Icon
          iconType="notification"
          color={totalUnReadNum !== 0 ? 'red' : 'grey'}
        />
        <Typography
          fontSize={10}
          fontWeight={500}
          color={totalUnReadNum !== 0 ? 'red' : 'grey'}
        >
          通知
        </Typography>
      </Stack>
    </>
  );
};

const CampSiteChangeLogNumberElement = ({ userId }: { userId: string }) => {
  const { data } = useCampSiteChangeLogTotalByUserIdQuery({
    user_id: userId,
  });

  return <>{data?.camp_site_change_log_aggregate.aggregate?.count ?? 0}</>;
};

export const MypageTop = ({ publicUser }: { publicUser: PublicUser }) => {
  const { isEdited } = useUserNotificationNum();
  const { data: point } = useMyPointQuery();
  // カンマ区切りにする
  const myPoint =
    point?.point_history_aggregate?.aggregate?.sum?.value?.toLocaleString() ??
    0;

  const router = useRouter();
  const { data: winGachaData } = useGetWinGachaQuery();

  const { user: loginUser, isLogin } = useGetLoginUser();
  if (!isLogin || !loginUser) {
    return <></>;
  }

  return (
    <Content>
      {publicUser && <UserImage user={publicUser} />}
      <Stack
        alignCenter
        sx={{
          padding: `0 ${theme.spacing(3)}`,
          height: '95px',
          background: '#ffffff',
        }}
      >
        <Grid container>
          <Grid item xs={8.4}>
            <Stack alignCenter height="100%">
              <Button
                onClick={() => router.push(`/camper/${loginUser.id}/history`)}
                buttonSize="md"
                color="white"
                startIcon={
                  <Stack center>
                    <Icon iconType="edit-pencil" />
                  </Stack>
                }
                endIcon={
                  <CountChip>
                    <Typography fontSize={10} fontWeight={500}>
                      <CampSiteChangeLogNumberElement userId={loginUser.id} />
                    </Typography>
                  </CountChip>
                }
                sx={{
                  padding: '5px 11px 5px 5px',
                  border: `solid 1px ${theme.palette.primary.main}`,
                }}
              >
                <Typography fontSize={13} fontWeight={500} color="primary">
                  編集・写真投稿した施設
                </Typography>
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={1.8}>
            <Stack center>
              <Button
                onClick={() => router.push(`/camper/${loginUser.id}/notice`)}
                component="label"
                sx={{ minWidth: '48px' }}
              >
                <Notification userId={loginUser.id} />
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={1.8}>
            <Stack center>
              <Button
                onClick={() =>
                  router.push(`/camper/${loginUser.id}/setting/edit`)
                }
                component="label"
                sx={{ minWidth: '48px' }}
              >
                {!isEdited && (
                  <Chip
                    label="1"
                    chipColor="red"
                    sx={{ position: 'absolute', top: '0px', right: '2px' }}
                  />
                )}
                <Stack column center space={0.5}>
                  <Icon
                    iconType="setting"
                    color={!loginUser.start_history ? 'red' : 'grey'}
                  />
                  <Typography
                    fontSize={10}
                    fontWeight={500}
                    color={!loginUser.start_history ? 'red' : 'grey'}
                  >
                    設定
                  </Typography>
                </Stack>
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
      <Box padding={3}>
        <Box>
          <Stack column paddingBottom={3}>
            <Board no_border="true">
              <Stack between paddingBottom={2}>
                <Typography fontSize={18} fontWeight={700}>
                  マイシーズ
                </Typography>
                <Typography
                  fontSize={16}
                  fontWeight={500}
                  color="primary"
                  onClick={() => router.push(`/camper/${loginUser.id}/seeds`)}
                  sx={{ cursor: 'pointer' }}
                >
                  詳細を見る
                </Typography>
              </Stack>
              <Divider />
              <Stack paddingTop={3} space={2.625} between alignCenter>
                <Stack space={2} center>
                  <Box
                    sx={{
                      maxWidth: '56px',
                      minWidth: '28px',
                    }}
                  >
                    <OptimizedImage
                      src="/assets/images/luckybazaar/luckybazaar_big.png"
                      alt="保有シーズ"
                      width="100%"
                      height="100%"
                    />
                  </Box>
                  <Stack
                    column
                    sx={{
                      minWidth: '74px',
                    }}
                  >
                    <Typography fontSize={14} fontWeight={500} lineHeight={1.4}>
                      保有シーズ
                    </Typography>
                    <Typography
                      fontSize={32}
                      fontWeight={900}
                      letterSpacing={0}
                      lineHeight={1.4}
                    >
                      {myPoint ?? 0}
                    </Typography>
                  </Stack>
                </Stack>
                <Box
                  sx={{
                    maxWidth: '146px',
                  }}
                >
                  <OptimizedImage
                    src="/assets/images/luckybazaar/luckybazaar_2.png"
                    alt="イラスト"
                    width="100%"
                    height="100%"
                  />
                </Box>
              </Stack>
            </Board>
          </Stack>
          <Box paddingBottom={5}>
            <Board no_border="true">
              <Stack between>
                <Stack space={2} alignCenter>
                  <Box
                    sx={{
                      objectFit: 'cover',
                      maxHeight: '36px',
                      height: '36px',
                    }}
                  >
                    <OptimizedImage
                      src="/assets/images/common/logo_circle_iihi.svg"
                      alt="イラスト"
                      width="100%"
                      height="100%"
                    />
                  </Box>
                  <Link href={`/camper/${loginUser.id}/gift`}>
                    <a>
                      <Typography
                        fontSize={18}
                        fontWeight={700}
                        color={theme.palette.primary.main}
                      >
                        当選したロトシーズ
                      </Typography>
                    </a>
                  </Link>
                </Stack>
                <Typography fontSize={21} fontWeight={900}>
                  {winGachaData?.physical_prize_aggregate.aggregate?.count ?? 0}
                </Typography>
              </Stack>
            </Board>
          </Box>
        </Box>
        <UserContents user={loginUser} />
      </Box>
    </Content>
  );
};

const query = gql`
  query CampSiteChangeLogTotalByUserID($user_id: String = "") {
    camp_site_change_log_aggregate(
      where: { changed_user_id: { _eq: $user_id } }
    ) {
      aggregate {
        count
      }
    }
  }
`;
